import React from 'react'
import { chipClasses, type Components, type ComponentsVariants, type Theme } from '@mui/material'

import { CloseIcon } from './icons'

type CybusChipVariants = {
    neutral: true
    primary: true
    success: true
    warning: true
    danger: true
    grey: true
}

declare module '@mui/material/Chip' {
    interface ChipPropsVariantOverrides extends CybusChipVariants {
        // Deprecates default MUI variants
        filled: false
        outlined: false
    }
}

const CHIP_HEIGHTS: Record<'small' | 'medium', number> = {
    small: 20,
    medium: 24,
}

const chipColorVariantStyle =
    (ramp: 'grey' | 'primary', borderShade: 200 | 300, hoverBorderShade: 300 | 700): NonNullable<ComponentsVariants<Theme>['MuiChip']>[number]['style'] =>
    ({ theme }: { theme: Theme }) => ({
        color: theme.palette[ramp][700],
        backgroundColor: theme.palette[ramp][100],
        border: `1px solid ${theme.palette[ramp][borderShade]}`,

        /** Only chips that do something react to hover; a plain chip (e.g. a status) stays still */
        [`&.${chipClasses.clickable}:hover:not(.${chipClasses.disabled}), &.${chipClasses.deletable}:hover:not(.${chipClasses.disabled})`]: {
            borderColor: theme.palette[ramp][hoverBorderShade],
            backgroundColor: theme.palette[ramp][200],
        },
        [`&.${chipClasses.focusVisible}`]: {
            borderColor: theme.palette.primary[700],
            backgroundColor: theme.palette[ramp][100],
            boxShadow: `0 0 0 2px ${theme.palette.primary[200]}`,
        },
        [`&.${chipClasses.disabled}`]: {
            borderColor: theme.palette[ramp][200],
            backgroundColor: theme.palette[ramp][100],
            opacity: 0.5,
        },
    })

/** Status chips are non-interactive, so unlike `chipColorVariantStyle` they define no hover, focus or disabled states */
const chipSoftVariantStyle =
    (ramp: 'success' | 'warning' | 'error' | 'grey'): NonNullable<ComponentsVariants<Theme>['MuiChip']>[number]['style'] => ({ theme }: { theme: Theme }) => ({
        color: theme.palette[ramp][ramp === 'grey' ? 700 : 800],
        backgroundColor: theme.palette[ramp][100],
        border: '1px solid transparent',
        padding: theme.spacing(0, 2),
        lineHeight: theme.typography.caption.lineHeight,

        [`& .${chipClasses.icon}`]: {
            color: theme.palette[ramp][500],
        },
    })

const variants: ComponentsVariants<Theme>['MuiChip'] = [
    {
        props: { variant: 'neutral' },
        style: chipColorVariantStyle('grey', 300, 300),
    },
    {
        props: { variant: 'primary' },
        style: chipColorVariantStyle('primary', 200, 700),
    },
    {
        props: { variant: 'success' },
        style: chipSoftVariantStyle('success'),
    },
    {
        props: { variant: 'warning' },
        style: chipSoftVariantStyle('warning'),
    },
    {
        props: { variant: 'danger' },
        style: chipSoftVariantStyle('error'),
    },
    {
        props: { variant: 'grey' },
        style: chipSoftVariantStyle('grey'),
    },
    {
        props: { size: 'small' },
        style: { height: CHIP_HEIGHTS.small },
    },
    {
        props: { size: 'medium' },
        style: { height: CHIP_HEIGHTS.medium },
    },
]

export const MuiChip: Components<Theme>['MuiChip'] = {
    defaultProps: {
        variant: 'neutral',
        deleteIcon: <CloseIcon />,
    },
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.caption,
            lineHeight: 2,
            boxSizing: 'border-box',
            borderRadius: 4,
            gap: theme.spacing(1),
            padding: theme.spacing(1, 2),

            [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: {
                color: 'inherit',
                fontSize: '1em',
                margin: 0,
            },
            [`& .${chipClasses.deleteIcon}:hover`]: {
                color: 'inherit',
                opacity: 0.7,
            },
            [`& .${chipClasses.label}`]: {
                padding: 0,
            },
        }),
    },
    variants,
}
